<template>
  <div>
    <template slot="header">
      <div class="row">
        <div class="col-6">
          <h3 class="mb-0">{{ $t("WAREHOUSES.WAREHOUSES_LIST") }}</h3>
        </div>
        <div class="col-6 text-right">
          <base-button
            type="primary"
            icon
            size="sm"
            @click="addWarehouse()"
            v-if="$currentUserCan($permissions.PERM_CREATE_WAREHOUSES)"
          >
            <span class="btn-inner--icon">
              <i class="fas fa-plus"></i>
            </span>
            <span class="btn-inner--text">
              {{ $t("WAREHOUSES.ADD_WAREHOUSE") }}
            </span>
          </base-button>
        </div>
      </div>
    </template>
    <div>
      <div
        class="col-12 d-flex flex-wrap filter-row"
      >
        <el-select
          class="select-primary pagination-select"
          v-model="pagination.perPage"
          :placeholder="$t('COMMON.PER_PAGE')"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <organization-selector
          v-if="
            !filterOrganization &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          @organizationChanged="
            (organizationId) => (selectedOrganization = organizationId)
          "
        />

        <base-input
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOCATIONS)"
          :placeholder="`${$t('COMMON.LOCATIONS')}`"
        >
          <locations-selector
            @locationsChanged="(locations) => (selectedLocations = locations)"
          />
        </base-input>

        <base-checkbox v-if="!modelUsed" v-model="isModel" class="mb-3">
          <span class="form-control-label">
            {{ $t("WAREHOUSES.WAREHOUSE_IS_MODEL") }}
          </span>
        </base-checkbox>

        <div>
          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.SEARCH_HINT.NAME_OR_DESCRIPTION')"
            clearable
          />
        </div>
      </div>

      <el-table
        class="warehouse-list-table table-responsive bg-grey text-white align-items-center table-flush"
        header-row-class-name="thead-light"
        :data="warehouses"
        @sort-change="sortChange"
        @selection-change="handleSelectionChange"
        :row-class-name="tableRowClassName"
      >
        <div slot="empty" v-if="loading">
          <img src="/img/loading.gif" style="height: 100px; width: 100px" />
        </div>
        <el-table-column v-if="isModel" type="selection" width="55" />
        <el-table-column
          :label="$t('COMMON.NAME')"
          prop="name"
          sortable="custom"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <router-link
              :to="{ name: 'View Warehouse', params: { id: row.id } }"
            >
              {{ `${row.name}` }}
            </router-link>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('WAREHOUSES.VALUE')"
          prop="value"
          min-width="150px"
        >
          <template slot-scope="{ row }">
            {{ $formatCurrency(row.value) }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.LOCATIONS')"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOCATIONS)"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <locations :locations="row.allowedLocations" />
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.ORGANIZATION')"
          v-if="
            !filterOrganization &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          min-width="250px"
        >
          <template v-slot="{ row }">
            <organization :organization="row.organization" />
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('WAREHOUSES.APPLIED_AT')"
          prop="created_at"
          sortable="custom"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <span v-if="row.applied_at">
              {{ $timeZoneDateFormat(row.applied_at) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.CREATED_AT')"
          prop="created_at"
          sortable="custom"
          min-width="250px"
        >
          <template v-slot="{ row }">
            {{ $timeZoneDateFormat(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column min-width="100px" align="center" fixed="right">
          <div slot-scope="{ row }" class="table-actions">
            <el-tooltip
              :content="$t('COMMON.VIEW')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_VIEW_WAREHOUSES)"
            >
              <a
                type="text"
                @click="viewWarehouse(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-eye"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.EDIT')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_EDIT_WAREHOUSES)"
            >
              <a
                type="text"
                @click="editWarehouse(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-edit"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.DELETE')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_DELETE_WAREHOUSES)"
            >
              <a
                type="text"
                @click="deleteWarehouse(row)"
                class="table-action table-action-delete"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-trash"></i>
              </a>
            </el-tooltip>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}

          <span v-if="selectedRows.length">
            &nbsp; &nbsp;
            {{ $t("COMMON.X_LINES_SELECTED", { count: selectedRows.length }) }}
          </span>
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Option,
  Select,
  Table,
  TableColumn,
  Tooltip,
} from "element-ui";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { BasePagination } from "@/components";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import LocationsSelector from "@/components/LocationsSelector.vue";

export default {
  name: "warehouse-list-table",

  components: {
    BasePagination,
    OrganizationSelector,
    LocationsSelector,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  props: {
    filterOrganization: {
      type: String,
      default: null,
      description: "Organization id",
    },
    customRemove: {
      type: Boolean,
      default: true,
      description: "Show remove button",
    },
    filterWarehouseableType: {
      type: String,
      default: null,
      description: "Warehouseable class",
    },
    filterWarehouseableId: {
      type: String,
      default: null,
      description: "Warehouseable id",
    },
    filterSupplierRuleSelected: {
      type: Array,
      default: null,
      description: "Supply Rule ids",
    },
    modelUsed: {
      type: String,
      default: null,
      description: "Warehouse model id",
    },
  },

  data() {
    return {
      query: null,
      selectedRows: [],
      sort: "-created_at",
      total: 0,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      warehouses: [],
      loading: true,
      isModel: false,
      selectedOrganization: null,
      selectedLocations: null,
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    pagination: {
      handler: "getList",
      immediate: false,
      deep: true,
    },
    filterOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterWarehouseableType: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterWarehouseableId: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedLocations: {
      handler: "getListDebounced",
      immediate: true,
    },
    isModel: {
      handler: "getListDebounced",
      immediate: true,
    },
    modelUsed: {
      handler: "getListDebounced",
      immediate: true,
    },
  },

  methods: {
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    async getList() {
      try {
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...(this.query ? { search: encodeURIComponent(this.query) } : {}),
            isModel: this.isModel,
          },
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
          include: "organization,allowedLocations,modelUsed",
        };

        if (this.filterOrganization) {
          params = {
            ...params,
            filter: { ...params.filter, organization: this.filterOrganization },
          };
        }
        if (this.selectedOrganization) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              organization: this.selectedOrganization,
            },
          };
        }
        if (this.filterWarehouseableType) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              warehouseableType: this.filterWarehouseableType,
            },
          };
        }
        if (this.filterWarehouseableId) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              warehouseableId: this.filterWarehouseableId,
            },
          };
        }
        if (this.selectedLocations) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              allowedLocations: this.selectedLocations.map((loc) => loc.id),
            },
          };
        }
        if (this.filterSupplierRuleSelected) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              supplyRules: this.filterSupplierRuleSelected.map(
                (item) => item.id
              ),
            },
          };
        }
        if (this.modelUsed) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              modelUsed: this.modelUsed,
            },
          };
        }

        await this.$store.dispatch("warehouses/list", params);
        this.warehouses = this.$store.getters["warehouses/list"];
        this.total = this.$store.getters["warehouses/listTotal"];
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async deleteWarehouse(warehouse) {
      const confirmation = await swal.fire({
        title: this.$t("WAREHOUSES.DELETE_THIS_WAREHOUSE"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          if (this.customRemove) {
            this.$emit("removeActionHandle", warehouse);
          } else {
            await this.$store.dispatch("warehouses/destroy", warehouse.id);
            await this.getList();
            this.$notify({
              type: "success",
              message: this.$t("WAREHOUSES.WAREHOUSE_DELETED"),
            });
          }
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    viewWarehouse(warehouse) {
      this.$router.push({
        name: "View Warehouse",
        params: { id: warehouse.id },
      });
    },

    async editWarehouse(row) {
      this.$router.push({
        name: "Edit Warehouse",
        params: { id: row.id },
      });
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },

    handleSelectionChange(value) {
      this.$emit("warehousesSelected", value);
    },

    tableRowClassName({ row, rowIndex }) {
      if (row.is_model) {
        return "is-model";
      }
      return "";
    },
  },
};
</script>

<style>
.warehouse-list-table tr.el-table__row.is-model {
  background-color: #e1e1e1e1;
}
</style>
